import { useQuery, useMutation, useQueryClient } from 'react-query';
import { packagesApi } from '../services/packages';
import {
  TrainingPackage,
  TrainingPackageCreate,
  TrainingPackageUpdate,
  ClientPackage,
  ClientPackageCreate,
  ClientPackageUpdate
} from '../types';
import { toast } from 'react-hot-toast';

// Хук для работы с пакетами тренировок
export const usePackages = (activeOnly: boolean = true) => {
  const queryClient = useQueryClient();

  // Получение списка пакетов
  const { data: packages = [], isLoading, error } = useQuery(
    ['packages', activeOnly],
    () => packagesApi.getPackages(activeOnly),
    {
      onError: (error: any) => {
        toast.error('Ошибка загрузки пакетов');
        console.error('Error fetching packages:', error);
      }
    }
  );

  // Создание пакета
  const createPackageMutation = useMutation(
    (data: TrainingPackageCreate) => packagesApi.createPackage(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('packages');
        toast.success('Пакет успешно создан');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка создания пакета');
      }
    }
  );

  // Обновление пакета
  const updatePackageMutation = useMutation(
    ({ packageId, data }: { packageId: number; data: TrainingPackageUpdate }) =>
      packagesApi.updatePackage(packageId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('packages');
        toast.success('Пакет успешно обновлен');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка обновления пакета');
      }
    }
  );

  // Удаление пакета
  const deletePackageMutation = useMutation(
    (packageId: number) => packagesApi.deletePackage(packageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('packages');
        toast.success('Пакет успешно удален');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка удаления пакета');
      }
    }
  );

  return {
    packages,
    isLoading,
    error,
    createPackage: createPackageMutation.mutate,
    updatePackage: updatePackageMutation.mutate,
    deletePackage: deletePackageMutation.mutate,
    isCreateLoading: createPackageMutation.isLoading,
    isUpdateLoading: updatePackageMutation.isLoading,
    isDeleteLoading: deletePackageMutation.isLoading
  };
};

// Хук для работы с пакетами клиента
export const useClientPackages = (clientId: number | null, activeOnly: boolean = true) => {
  const queryClient = useQueryClient();

  const { data: clientPackages = [], isLoading, error } = useQuery(
    ['clientPackages', clientId, activeOnly],
    () => clientId ? packagesApi.getClientPackages(clientId, activeOnly) : Promise.resolve([]),
    {
      enabled: !!clientId,
      onError: (error: any) => {
        toast.error('Ошибка загрузки пакетов клиента');
        console.error('Error fetching client packages:', error);
      }
    }
  );

  // Назначение пакета клиенту
  const assignPackageMutation = useMutation(
    ({ packageId, data }: { packageId: number; data: ClientPackageCreate }) =>
      packagesApi.assignPackageToClient(packageId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clientPackages');
        queryClient.invalidateQueries('packages');
        toast.success('Пакет успешно назначен клиенту');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка назначения пакета');
      }
    }
  );

  // Обновление пакета клиента
  const updateClientPackageMutation = useMutation(
    ({ clientPackageId, data }: { clientPackageId: number; data: ClientPackageUpdate }) =>
      packagesApi.updateClientPackage(clientPackageId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clientPackages');
        toast.success('Пакет клиента успешно обновлен');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка обновления пакета клиента');
      }
    }
  );

  // Использование сессии из пакета
  const useSessionMutation = useMutation(
    (clientPackageId: number) => packagesApi.usePackageSession(clientPackageId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('clientPackages');
        toast.success(`Тренировка использована. Осталось: ${data.sessions_remaining}`);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка использования тренировки');
      }
    }
  );

  return {
    clientPackages,
    isLoading,
    error,
    assignPackage: assignPackageMutation.mutate,
    updateClientPackage: updateClientPackageMutation.mutate,
    useSession: useSessionMutation.mutate,
    isAssignLoading: assignPackageMutation.isLoading,
    isUpdateLoading: updateClientPackageMutation.isLoading,
    isUseSessionLoading: useSessionMutation.isLoading
  };
};